import { z } from "zod";

// Expenses: what the company spends on a job. A supplier invoice, a tech's
// store receipt, or an office entry becomes one expense, tied to the jobs it
// was bought for, and posts to QuickBooks as a vendor Bill exactly the way
// skuru posts CSG's bills (docs/qb-expenses-audit.md). Written by functions
// only; the office reads them, techs read the ones on their jobs.

export const SurchargeTypeSchema = z.enum(["shipping", "fuel", "tax", "adjustment", "other"]);
export type SurchargeType = z.infer<typeof SurchargeTypeSchema>;
export const SURCHARGE_TYPES = SurchargeTypeSchema.options;

/**
 * expense: its own Bill line to the surcharge's account.
 * distribute: rolled into the parts' cost. QuickBooks Desktop leaves it off the
 *   bill (skuru behavior); QuickBooks Online still posts it as its own line.
 * ignore: never posted.
 */
export const SurchargeHandlingSchema = z.enum(["expense", "distribute", "ignore"]);
export type SurchargeHandling = z.infer<typeof SurchargeHandlingSchema>;

/** skuru's defaults; a company's own choices live in QbBillSettings.surchargeHandling. */
export const DEFAULT_SURCHARGE_HANDLING: Record<SurchargeType, SurchargeHandling> = {
  shipping: "expense",
  fuel: "expense",
  tax: "expense",
  adjustment: "distribute",
  other: "expense",
};

export const SURCHARGE_LABEL: Record<SurchargeType, string> = {
  shipping: "Freight / shipping",
  fuel: "Fuel surcharge",
  tax: "Sales tax",
  adjustment: "Adjustment / discount",
  other: "Other fees",
};

export const ExpenseLineSchema = z.object({
  id: z.string().min(1).max(40),
  description: z.string().min(1).max(500),
  quantity: z.number().positive().max(100000),
  // Negative allowed: credits and returns on a supplier invoice.
  unitCostCents: z.number().int().min(-100000000).max(100000000),
  /** The supplier's own line total; QuickBooks "expenses" mode posts exactly this. */
  extendedCents: z.number().int().min(-1000000000).max(1000000000),
  sku: z.string().max(80).default(""),
  /** The job this part was bought for (job costing in Clevis). */
  jobId: z.string().nullable().default(null),
  orderedItemId: z.string().nullable().default(null),
  /** Pricebook item, when known: its QuickBooks item link is remembered there. */
  itemId: z.string().nullable().default(null),
  /** part: something bought. referral: a referral reward paid to a customer (Clevis Relay) — never a QuickBooks item. */
  kind: z.enum(["part", "referral"]).default("part"),
});
export type ExpenseLine = z.infer<typeof ExpenseLineSchema>;

export const ExpenseSurchargeSchema = z.object({
  id: z.string().min(1).max(40),
  type: SurchargeTypeSchema,
  label: z.string().min(1).max(200),
  amountCents: z.number().int().min(-100000000).max(100000000),
  handling: SurchargeHandlingSchema,
});
export type ExpenseSurcharge = z.infer<typeof ExpenseSurchargeSchema>;

/** One attempt to post the expense to QuickBooks (history, newest last). */
export const ExpensePushSchema = z.object({
  target: z.enum(["desktop", "online"]),
  at: z.number(),
  byUid: z.string(),
  status: z.enum(["queued", "synced", "error"]),
  chainId: z.string().nullable().default(null),
  txnId: z.string().nullable().default(null),
  refNumber: z.string().nullable().default(null),
  operation: z.enum(["create", "update"]).default("create"),
  /** Desktop only: a reply the Web Connector lost is verified with a BillQuery. */
  confirmation: z.enum(["confirmed", "pending", "unconfirmed"]).nullable().default(null),
  message: z.string().max(1000).nullable().default(null),
});
export type ExpensePush = z.infer<typeof ExpensePushSchema>;

/** The observable QuickBooks state, like invoices' qbd/qbo blocks. */
export const ExpenseQbStateSchema = z.object({
  status: z.enum(["queued", "synced", "error", "unconfirmed"]),
  txnId: z.string().nullable().default(null),
  refNumber: z.string().nullable().default(null),
  chainId: z.string().nullable().default(null),
  lastSyncAt: z.number(),
  lastError: z.string().nullable().default(null),
  warnings: z.array(z.string()).default([]),
});
export type ExpenseQbState = z.infer<typeof ExpenseQbStateSchema>;

export const ExpenseSourceSchema = z.enum(["receiving", "receipt", "manual", "referral"]);
export type ExpenseSource = z.infer<typeof ExpenseSourceSchema>;

export const ExpenseSchema = z.object({
  number: z.number().int().positive(),
  vendorId: z.string().nullable().default(null),
  vendorName: z.string().min(1).max(160),
  /** The supplier's invoice / receipt number: the QuickBooks bill's reference number. */
  invoiceNumber: z.string().max(40).default(""),
  /** Supplier invoice date (ms, local noon); the bill's TxnDate. */
  invoiceDate: z.number().nullable().default(null),
  /** Primary job (receipts and single-job invoices); jobIds lists every job on any line. */
  jobId: z.string().nullable().default(null),
  jobIds: z.array(z.string()).max(200).default([]),
  lines: z.array(ExpenseLineSchema).max(200),
  surcharges: z.array(ExpenseSurchargeSchema).max(30).default([]),
  // Server-computed, never trusted from clients.
  linesCents: z.number().int(),
  surchargesCents: z.number().int(),
  totalCents: z.number().int(),
  /** draft: still being checked (a tech's receipt starts here). approved: ready for QuickBooks. */
  status: z.enum(["draft", "approved"]),
  source: ExpenseSourceSchema,
  /** Storage path of the scanned invoice or receipt. */
  documentPath: z.string().nullable().default(null),
  note: z.string().max(2000).default(""),
  createdByUid: z.string(),
  createdByName: z.string().default(""),
  approvedByUid: z.string().nullable().default(null),
  approvedAt: z.number().nullable().default(null),
  qbd: ExpenseQbStateSchema.nullable().default(null),
  qbo: ExpenseQbStateSchema.nullable().default(null),
  pushes: z.array(ExpensePushSchema).max(50).default([]),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Expense = z.infer<typeof ExpenseSchema>;

export function computeExpenseTotals(lines: ExpenseLine[], surcharges: ExpenseSurcharge[]) {
  const linesCents = lines.reduce((s, l) => s + l.extendedCents, 0);
  // Every surcharge is real money on the supplier's invoice, whatever its
  // QuickBooks handling; "ignore" only means it never posts.
  const surchargesCents = surcharges.reduce((s, x) => s + x.amountCents, 0);
  return { linesCents, surchargesCents, totalCents: linesCents + surchargesCents };
}

// ---------- QuickBooks bill settings ----------

/** A QuickBooks list entry: Desktop ListID or Online Id, plus its full name. */
export const QbRefSchema = z.object({ id: z.string().min(1).max(60), name: z.string().min(1).max(260) });
export type QbRef = z.infer<typeof QbRefSchema>;

export const QbNewItemTypeSchema = z.enum(["NonInventory", "Service", "OtherCharge", "Inventory"]);
export type QbNewItemType = z.infer<typeof QbNewItemTypeSchema>;

/**
 * How expenses post as Bills. Stored per company on
 * integrationConfigs/{quickbooks-desktop|quickbooks-online}.billSettings and
 * written only by setQbBillSettings (owner/admin).
 */
export const QbBillSettingsSchema = z.object({
  /**
   * items: each line is an item on the bill's Items tab (matched, or created
   *   with newItemDefaults).
   * expenses: each line posts its full amount to expenseModeAccount on the
   *   Expenses tab; nothing is matched or created. (skuru "Bill all items as
   *   expenses", which is how CSG posts.)
   */
  billLineMode: z.enum(["items", "expenses"]).default("items"),
  expenseModeAccount: QbRefSchema.nullable().default(null),
  /** Referral rewards (Clevis Relay) post here, in either mode (e.g. Advertising & Marketing). */
  referralRewardsAccount: QbRefSchema.nullable().default(null),
  surchargeAccounts: z
    .object({
      shipping: QbRefSchema.nullable().default(null),
      fuel: QbRefSchema.nullable().default(null),
      tax: QbRefSchema.nullable().default(null),
      adjustment: QbRefSchema.nullable().default(null),
      other: QbRefSchema.nullable().default(null),
    })
    .default({}),
  surchargeHandling: z
    .object({
      shipping: SurchargeHandlingSchema.default("expense"),
      fuel: SurchargeHandlingSchema.default("expense"),
      tax: SurchargeHandlingSchema.default("expense"),
      adjustment: SurchargeHandlingSchema.default("distribute"),
      other: SurchargeHandlingSchema.default("expense"),
    })
    .default({}),
  newItemDefaults: z
    .object({
      type: QbNewItemTypeSchema.default("NonInventory"),
      incomeAccount: QbRefSchema.nullable().default(null),
      expenseAccount: QbRefSchema.nullable().default(null),
      assetAccount: QbRefSchema.nullable().default(null),
    })
    .default({}),
  /** Tag each bill line with the job's Customer (QuickBooks job costing). Off = skuru behavior. */
  jobCosting: z.boolean().default(false),
  /** Job-costed lines are marked billable to the customer (else not billable). */
  jobCostingBillable: z.boolean().default(false),

  // ----- Everything below has a default that matches skuru; nothing is fixed. -----

  /**
   * What "spread into the parts" (distribute) charges do.
   *   omit: left off the bill (skuru on QuickBooks Desktop)
   *   ownLine: posted as their own line to the charge's account (skuru on Online)
   *   spread: added into the part lines, in proportion to each line's amount
   * null = that version's skuru behavior (Desktop omit, Online ownLine).
   */
  distributeMode: z.enum(["omit", "ownLine", "spread"]).nullable().default(null),
  /** A line that is only "FREIGHT", "Fuel surcharge"… posts like that charge (skuru: always). */
  rerouteFeeLines: z.boolean().default(true),
  /** The company's own fee-line names, on top of the built-in freight/fuel list. */
  extraFeeLines: z
    .array(z.object({ phrase: z.string().trim().min(2).max(60), type: SurchargeTypeSchema }))
    .max(30)
    .default([]),
  /** Supplier invoice number longer than QuickBooks allows: keep the first / last characters, or let QuickBooks number it. */
  refNumberOverflow: z.enum(["first", "last", "omit"]).default("first"),
  /** Bill memo. Tokens: {expense} {job} {vendor} {invoice}. */
  memoTemplate: z.string().max(200).default("Clevis - expense {expense} - {job}"),
  /** A vendor QuickBooks doesn't have yet: create it, or wait for someone to pick one. */
  newVendors: z.enum(["create", "pick"]).default("create"),
  /** Class on every bill line (QuickBooks class tracking). skuru sends none. */
  billClass: QbRefSchema.nullable().default(null),
  updatedAt: z.number().default(0),
});

export const DEFAULT_MEMO_TEMPLATE = "Clevis - expense {expense} - {job}";

/** The memo with its tokens filled; separators around an empty token collapse. */
export function renderBillMemo(
  template: string,
  values: { expense: number; job: string; vendor: string; invoice: string },
): string {
  const fill = (s: string) =>
    s
      .replace(/\{expense\}/g, String(values.expense))
      .replace(/\{job\}/g, values.job)
      .replace(/\{vendor\}/g, values.vendor)
      .replace(/\{invoice\}/g, values.invoice)
      .trim();
  // Split the TEMPLATE on its " - " / " | " separators before filling, so a
  // piece that comes out empty drops along with its separator (and a job
  // title that itself contains " - " is left alone).
  const pieces = (template.trim() || DEFAULT_MEMO_TEMPLATE).split(/(\s+[-|·]\s+)/);
  let out = "";
  let sep = "";
  pieces.forEach((piece, i) => {
    if (i % 2 === 1) {
      sep = piece;
      return;
    }
    const text = fill(piece);
    if (text) out += out ? sep + text : text;
  });
  return out.replace(/\s{2,}/g, " ").trim();
}
export type QbBillSettings = z.infer<typeof QbBillSettingsSchema>;

// ---------- QuickBooks list caches (Desktop) ----------

export const QbdListKindSchema = z.enum(["vendor", "item", "account", "class"]);
export type QbdListKind = z.infer<typeof QbdListKindSchema>;

export const QbdCachedEntrySchema = z.object({
  listId: z.string(),
  /** FullName (colon-joined for sub-entries). */
  name: z.string(),
  /** Vendors: company; items: type (Service/NonInventory/Inventory/OtherCharge); accounts: AccountType. */
  type: z.string().default(""),
  /** Items: ManufacturerPartNumber (skuru keeps the SKU there). */
  sku: z.string().default(""),
});
export type QbdCachedEntry = z.infer<typeof QbdCachedEntrySchema>;

// ---------- Matching helpers (ported from skuru, shared so client previews agree) ----------

/** skuru's normalizeIntegrationVendorName: lowercase, punctuation out, business suffixes dropped. */
export function normalizeVendorName(raw: string): string {
  if (!raw) return "";
  const SUFFIXES = new Set([
    "inc",
    "incorporated",
    "llc",
    "ltd",
    "limited",
    "co",
    "corp",
    "corporation",
    "company",
    "lp",
    "llp",
    "the",
  ]);
  return raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 0 && !SUFFIXES.has(t))
    .join(" ")
    .trim();
}

/** skuru's normalizeItemName: `8" Top Seal` and `8' Top Seal` both → "8 top seal". */
export function normalizeItemName(raw: string): string {
  if (!raw) return "";
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// skuru's genericLineItem whitelist: a line that IS freight or fuel (the whole
// description, after normalizing) posts like a surcharge, to the company's
// freight/fuel account, instead of as a part. "Freight Connector" is a part.
const GENERIC_LINE_PATTERNS: Record<string, "shipping" | "fuel"> = {
  freight: "shipping",
  "freight charge": "shipping",
  "freight charges": "shipping",
  shipping: "shipping",
  "shipping charge": "shipping",
  "shipping charges": "shipping",
  "shipping and handling": "shipping",
  "s and h": "shipping",
  delivery: "shipping",
  "delivery charge": "shipping",
  "delivery charges": "shipping",
  handling: "shipping",
  "handling fee": "shipping",
  "handling charge": "shipping",
  fuel: "fuel",
  "fuel surcharge": "fuel",
  "fuel surcharges": "fuel",
  "fuel charge": "fuel",
};

/** skuru's fee-line normalizer: "S&H", "S & H", "s and h" all → "s and h". */
export function normalizeFeePhrase(s: string): string {
  return s
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[.,:;/\\\-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function classifyGenericLine(description: string | null | undefined): "shipping" | "fuel" | null {
  if (!description) return null;
  return GENERIC_LINE_PATTERNS[normalizeFeePhrase(description)] ?? null;
}

/** The built-in list plus a company's own fee-line names (exact match after normalizing). */
export function classifyFeeLine(
  description: string | null | undefined,
  extra: Array<{ phrase: string; type: SurchargeType }> = [],
): SurchargeType | null {
  if (!description) return null;
  const norm = normalizeFeePhrase(description);
  const own = extra.find((x) => normalizeFeePhrase(x.phrase) === norm);
  return own?.type ?? GENERIC_LINE_PATTERNS[norm] ?? null;
}
